import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useEvents } from '../../hooks/useEvents'
import { parseIsoDate } from '../../lib/dates'

// MiniCalendar - current month grid with a coloured dot on days that have at
// least one event covering them. Click a day with events to jump to
// /calendar?date=YYYY-MM-DD (spec 13 wires that page; the link still works as
// a deep-link target for now).
//
// Pure presentation - derives data from the existing useEvents cache, no
// extra API call.

const WEEKDAY_HEAD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function MiniCalendar() {
  const { data: events } = useEvents()
  const today = new Date()
  const year = today.getUTCFullYear()
  const month = today.getUTCMonth()

  // First day of the month, in UTC. JS getUTCDay() returns 0..6 with Sunday=0;
  // we want Monday=0 for a UK-conventional grid.
  const firstUtc = new Date(Date.UTC(year, month, 1))
  const offset = (firstUtc.getUTCDay() + 6) % 7    // Sun(0)->6, Mon(1)->0, ...
  const lastUtc = new Date(Date.UTC(year, month + 1, 0))    // day 0 of next month = last of current
  const dayCount = lastUtc.getUTCDate()

  // Map date -> array of type bg colours covering it. Used to decide whether
  // to show a dot and what colour to draw it.
  const dotMap = useMemo(() => {
    const m = new Map<string, string[]>()
    if (!events) return m
    for (const e of events) {
      if (e.status === 'Idea') continue // Ideas stay off the dashboard calendar
      const start = parseIsoDate(e.startDate)
      const end = parseIsoDate(e.endDate)
      const cur = new Date(start.getTime())
      while (cur.getTime() <= end.getTime()) {
        if (cur.getUTCFullYear() === year && cur.getUTCMonth() === month) {
          const key = isoDate(cur)
          const arr = m.get(key) ?? []
          arr.push(e.type.colourBorder)
          m.set(key, arr)
        }
        cur.setUTCDate(cur.getUTCDate() + 1)
      }
    }
    return m
  }, [events, year, month])

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-5 py-3 border-b border-line">
        <h2 className="font-display text-lg">
          {firstUtc.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
        </h2>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-wide text-ink-soft font-semibold mb-1">
          {WEEKDAY_HEAD.map((w) => (
            <div key={w} className="py-1">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`pad-${i}`} aria-hidden />
          ))}
          {Array.from({ length: dayCount }).map((_, i) => {
            const day = i + 1
            const iso = isoDate(new Date(Date.UTC(year, month, day)))
            const dots = dotMap.get(iso)
            const isToday =
              day === today.getDate() &&
              month === today.getMonth() &&
              year === today.getFullYear()
            const cell = (
              <div
                className={
                  'aspect-square flex flex-col items-center justify-center rounded text-xs ' +
                  (isToday ? 'bg-green text-parchment font-semibold' : 'hover:bg-parchment')
                }
              >
                <span>{day}</span>
                {dots && dots.length > 0 && (
                  <span className="flex gap-0.5 mt-0.5" aria-label={`${dots.length} event${dots.length === 1 ? '' : 's'}`}>
                    {dots.slice(0, 3).map((c, di) => (
                      <span
                        key={di}
                        className="inline-block w-1 h-1 rounded-full"
                        style={{ background: c }}
                        aria-hidden
                      />
                    ))}
                  </span>
                )}
              </div>
            )
            return dots && dots.length > 0 ? (
              <Link
                key={day}
                to={`/calendar?date=${iso}`}
                className="block"
                title={`${dots.length} event${dots.length === 1 ? '' : 's'} on ${iso}`}
              >
                {cell}
              </Link>
            ) : (
              <div key={day}>{cell}</div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function isoDate(d: Date): string {
  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
